import { TableHead } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";

export type SortDir = "asc" | "desc";

/**
 * A column header that sorts. Click a new column → its natural first direction;
 * click the active one → flip. `aria-sort` is set on the active header only.
 */
export function SortableHead<K extends string>({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  className,
  align = "left",
  testId,
  title,
}: {
  label: string;
  sortKey: K;
  activeKey: K | null;
  dir: SortDir;
  onSort: (key: K) => void;
  className?: string;
  align?: "left" | "center" | "right";
  testId?: string;
  /** Tooltip on the header (e.g. what the column measures). */
  title?: string;
}) {
  const active = activeKey === sortKey;
  const justify =
    align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <TableHead
      className={className}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : undefined}
      data-testid={testId}
      title={title}
    >
      <button
        type="button"
        className={cn(
          "inline-flex w-full items-center gap-1 rounded-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          justify,
          active && "text-foreground",
        )}
        onClick={() => onSort(sortKey)}
      >
        {label}
        {active ? (
          dir === "asc" ? (
            <ArrowUp className="w-3.5 h-3.5" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5" />
          )
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />
        )}
      </button>
    </TableHead>
  );
}
